import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { formatPhone, formatPhoneInput } from "@/lib/phone";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { UserPlus, Trash2, Pencil, Mail, Copy, CheckCircle2, AlertTriangle, Search, Car } from "lucide-react";
import { toast } from "sonner";
import { HipaaSendButton } from "@/components/HipaaSendButton";

const ROLES = [
  { v: "pharmacy_staff", label: "Pharmacy Staff" },
  { v: "dispatcher", label: "Dispatcher" },
  { v: "driver", label: "Driver" },
  { v: "super_admin", label: "Super Admin" },
];

// Filter options for the role pill row. "all" + the 4 user roles.
const ROLE_FILTERS = [
  { v: "all", label: "All", testid: "users-filter-all" },
  { v: "driver", label: "Drivers", testid: "users-filter-drivers" },
  { v: "dispatcher", label: "Dispatchers", testid: "users-filter-dispatchers" },
  { v: "pharmacy_staff", label: "Staff", testid: "users-filter-staff" },
  { v: "super_admin", label: "Admins", testid: "users-filter-admins" },
];

// Workspace / region options. Kept in a single place so the filter dropdown,
// row badge, and edit dialog all read the same labels + testids.
const REGIONS = [
  { v: "south_fl", label: "South FL", short: "South", tone: "bg-sky-100 text-sky-800 border-sky-200" },
  { v: "central_fl", label: "Central FL", short: "Central", tone: "bg-amber-100 text-amber-800 border-amber-200" },
];
const REGION_BY_VALUE = Object.fromEntries(REGIONS.map((r) => [r.v, r]));

// Color name → CSS color (matches the AdminDrivers swatch palette).
const VEHICLE_COLOR_HEX = {
  Black: "#111111", White: "#f4f4f5", Silver: "#c0c0c0", Gray: "#6b7280",
  Red: "#dc2626", Blue: "#2563eb", Green: "#16a34a", Yellow: "#eab308",
  Orange: "#ea580c", Brown: "#92400e", Beige: "#d6c79c",
};
const VEHICLE_COLOR_OPTIONS = Object.keys(VEHICLE_COLOR_HEX);

export default function AdminUsers() {
  const [rows, setRows] = useState([]);
  const [pharmacies, setPharmacies] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "driver", pharmacy_id: "", phone: "", region: "south_fl" });
  const [busy, setBusy] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", email: "", phone: "", password: "", role: "driver", pharmacy_id: "", region: "south_fl", veh_plate: "", veh_model: "", veh_color: "White" });

  // Filters — name search (case-insensitive), role pill, pharmacy dropdown.
  // Persist nothing for now (small list); add localStorage later if requested.
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [pharmacyFilter, setPharmacyFilter] = useState("all"); // "all" | "none" | pharmacy_id
  const [regionFilter, setRegionFilter] = useState("all"); // "all" | "south_fl" | "central_fl" | "unset"

  const [hipaaByDriver, setHipaaByDriver] = useState({});
  const loadHipaa = () => api.get("/hipaa/drivers")
    .then((r) => setHipaaByDriver(Object.fromEntries((r.data || []).map((x) => [x.driver_id, x]))))
    .catch(() => {});
  const load = () => Promise.all([
    api.get("/users", { params: { include_inactive: true } }),
    api.get("/pharmacies", { params: { include_inactive: true } }),
    api.get("/vehicles").catch(() => ({ data: [] })),
  ]).then(([u, p, v]) => {
    setRows(u.data); setPharmacies(p.data); setVehicles(v.data || []);
    loadHipaa();
  });
  useEffect(() => { load(); }, []);

  // Quick lookup: driver_id → vehicle row, used by the inline vehicle column.
  const vehicleByDriverId = React.useMemo(() => {
    const m = {};
    for (const v of vehicles) if (v?.driver_id) m[v.driver_id] = v;
    return m;
  }, [vehicles]);

  // Apply all four filters in one pass.
  const filteredRows = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((u) => {
      if (roleFilter !== "all" && u.role !== roleFilter) return false;
      if (pharmacyFilter === "none") {
        if (u.pharmacy_id) return false;
      } else if (pharmacyFilter !== "all") {
        if (u.pharmacy_id !== pharmacyFilter) return false;
      }
      if (regionFilter === "unset") {
        if (u.region) return false;
      } else if (regionFilter !== "all") {
        if ((u.region || "") !== regionFilter) return false;
      }
      if (q) {
        const hay = `${u.name || ""} ${u.email || ""} ${u.phone || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, search, roleFilter, pharmacyFilter, regionFilter]);

  const toggleActive = async (u, next) => {
    try {
      await api.patch(`/users/${u.id}`, { active: next });
      toast.success(next ? `${u.name} activated` : `${u.name} deactivated`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const [emailingId, setEmailingId] = useState(null);
  const [inviteResult, setInviteResult] = useState(null); // { user, email_sent, fallback_url, to, expires_in_days, error, copied }

  const sendInviteEmail = async (u) => {
    if (!u.email) { toast.error("This user has no email on file"); return; }
    setEmailingId(u.id);
    try {
      const { data } = await api.post(`/users/${u.id}/send-invite-email`);
      setInviteResult({ user: u, ...data, copied: false });
      if (data.email_sent) toast.success(`Invite emailed to ${data.to}`);
      else toast.warning("Couldn't email — share the magic link manually");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setEmailingId(null); }
  };

  const copyInvite = async () => {
    if (!inviteResult?.fallback_url) return;
    try {
      await navigator.clipboard.writeText(inviteResult.fallback_url);
      setInviteResult({ ...inviteResult, copied: true });
      toast.success("Invite link copied");
      setTimeout(() => setInviteResult((r) => r ? { ...r, copied: false } : r), 2500);
    } catch { toast.error("Could not copy link"); }
  };

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { ...form, pharmacy_id: form.pharmacy_id || null };
      await api.post("/users", body);
      toast.success("User created");
      setForm({ name: "", email: "", password: "", role: "driver", pharmacy_id: "", phone: "", region: "south_fl" });
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!confirm("Delete this user?")) return;
    await api.delete(`/users/${id}`);
    load();
  };

  const startEdit = (u) => {
    setEditTarget(u);
    // Prefill from any existing vehicle assigned to this driver so admins
    // can update plate/model/color in the same dialog. For non-drivers the
    // vehicle section is hidden entirely.
    const veh = u.role === "driver" ? vehicleByDriverId[u.id] : null;
    setEditForm({
      name: u.name || "", email: u.email || "", phone: u.phone || "",
      password: "", role: u.role || "driver", pharmacy_id: u.pharmacy_id || "",
      region: u.region || "south_fl",
      veh_plate: veh?.plate || "",
      veh_model: veh?.model || "",
      veh_color: veh?.color || "White",
    });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editTarget) return;
    setBusy(true);
    try {
      const updates = {};
      if (editForm.name !== editTarget.name) updates.name = editForm.name;
      if (editForm.email !== editTarget.email) updates.email = editForm.email;
      if ((editForm.phone || "") !== (editTarget.phone || "")) updates.phone = editForm.phone;
      if (editForm.role !== editTarget.role) updates.role = editForm.role;
      if ((editForm.pharmacy_id || "") !== (editTarget.pharmacy_id || "")) updates.pharmacy_id = editForm.pharmacy_id || null;
      if ((editForm.region || "") !== (editTarget.region || "")) updates.region = editForm.region;
      if (editForm.password) updates.password = editForm.password;
      if (Object.keys(updates).length) await api.patch(`/users/${editTarget.id}`, updates);

      // Vehicle upsert — only when role is driver AND at least one vehicle
      // field is filled. Reuses the same {"—"} placeholder convention as
      // AdminDrivers.jsx so downstream lookups remain consistent.
      if (editForm.role === "driver") {
        const veh = vehicleByDriverId[editTarget.id];
        const anyVehicleField = (editForm.veh_plate || "").trim()
          || (editForm.veh_model || "").trim()
          || (editForm.veh_color && editForm.veh_color !== "White");
        const changed = !veh
          ? anyVehicleField
          : (
              (editForm.veh_plate || "") !== (veh.plate || "")
              || (editForm.veh_model || "") !== (veh.model || "")
              || (editForm.veh_color || "") !== (veh.color || "")
            );
        if (changed) {
          const payload = {
            plate: (editForm.veh_plate || "").trim() || "—",
            model: (editForm.veh_model || "").trim() || "—",
            color: editForm.veh_color || "White",
            driver_id: editTarget.id,
          };
          if (veh) await api.patch(`/vehicles/${veh.id}`, payload);
          else await api.post("/vehicles", payload);
        }
      }

      toast.success("User updated");
      setEditTarget(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-6 lg:p-10 max-w-6xl mx-auto" data-testid="admin-users-page">
      <header className="flex items-end justify-between mb-6">
        <div>
          <h1 className="text-3xl font-display font-bold tracking-tight">Team members</h1>
          <p className="text-muted-foreground text-sm mt-1">Manage admins, dispatchers, pharmacy staff and drivers.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full" data-testid="add-user-btn"><UserPlus className="h-4 w-4 mr-1.5" /> Add user</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New team member</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-3" data-testid="add-user-form">
              <div><Label>Full name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Email</Label><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email" /></div>
                <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: formatPhoneInput(e.target.value) })} placeholder="305-555-1234" data-testid="user-phone" /></div>
              </div>
              <div><Label>Password</Label><Input type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Role</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ROLES.map((r) => <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Pharmacy</Label>
                  <Select value={form.pharmacy_id || "none"} onValueChange={(v) => setForm({ ...form, pharmacy_id: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="user-pharmacy"><SelectValue placeholder="None" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— None —</SelectItem>
                      {pharmacies.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Workspace / region</Label>
                <Select value={form.region || "south_fl"} onValueChange={(v) => setForm({ ...form, region: v })}>
                  <SelectTrigger data-testid="user-region"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="south_fl">South FL — Broward + Miami-Dade</SelectItem>
                    <SelectItem value="central_fl">Central FL — Orlando + Kissimmee</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" disabled={busy} className="w-full rounded-full" data-testid="user-submit">{busy ? "Creating…" : "Create user"}</Button>
            </form>
          </DialogContent>
        </Dialog>
      </header>

      {/* Filter bar — name search + role pills + pharmacy dropdown. */}
      <Card className="rounded-xl border-border bg-card/60 p-3 mb-4 flex flex-wrap items-center gap-3" data-testid="users-filter-bar">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search name, email or phone…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-9"
            data-testid="users-search"
          />
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          {ROLE_FILTERS.map((f) => (
            <button
              key={f.v}
              type="button"
              onClick={() => setRoleFilter(f.v)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                roleFilter === f.v
                  ? "bg-primary text-primary-foreground"
                  : "bg-secondary/60 text-muted-foreground hover:bg-secondary"
              }`}
              data-testid={f.testid}
            >
              {f.label}
            </button>
          ))}
        </div>
        <Select value={pharmacyFilter} onValueChange={setPharmacyFilter}>
          <SelectTrigger className="w-[200px] h-9" data-testid="users-filter-pharmacy">
            <SelectValue placeholder="All pharmacies" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All pharmacies</SelectItem>
            <SelectItem value="none">— No pharmacy —</SelectItem>
            {pharmacies.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={regionFilter} onValueChange={setRegionFilter}>
          <SelectTrigger className="w-[160px] h-9" data-testid="users-filter-region">
            <SelectValue placeholder="All areas" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All areas</SelectItem>
            {REGIONS.map((r) => (
              <SelectItem key={r.v} value={r.v} data-testid={`users-filter-region-${r.v}`}>{r.label}</SelectItem>
            ))}
            <SelectItem value="unset">— No area set —</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-xs text-muted-foreground ml-auto" data-testid="users-filter-count">
          {filteredRows.length} / {rows.length}
        </span>
      </Card>

      <Card className="rounded-xl border-border bg-card/80 overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[1080px]">
          <colgroup>
            <col className="w-[15%]" />{/* Name */}
            <col className="w-[22%]" />{/* Email */}
            <col className="w-[10%]" />{/* Role */}
            <col className="w-[8%]" />{/* Area */}
            <col className="w-[15%]" />{/* Pharmacy */}
            <col className="w-[16%]" />{/* Vehicle */}
            <col className="w-[6%]" />{/* Active */}
            <col className="w-[8%]" />{/* Actions */}
          </colgroup>
          <thead className="bg-secondary/50 text-left">
            <tr>
              <th className="px-3 py-3 font-medium">Name</th>
              <th className="px-3 py-3 font-medium">Email</th>
              <th className="px-3 py-3 font-medium">Role</th>
              <th className="px-3 py-3 font-medium">Area</th>
              <th className="px-3 py-3 font-medium">Pharmacy</th>
              <th className="px-3 py-3 font-medium">Vehicle</th>
              <th className="px-3 py-3 font-medium text-center">Active</th>
              <th className="px-3 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.map((u) => {
              const ph = pharmacies.find((p) => p.id === u.pharmacy_id);
              const isActive = u.active !== false;
              const veh = u.role === "driver" ? vehicleByDriverId[u.id] : null;
              return (
                <tr key={u.id} className={`border-t border-border align-top ${!isActive ? "opacity-60" : ""}`} data-testid={`user-row-${u.id}`}>
                  <td className="px-3 py-3 font-medium break-words">
                    {u.name}
                    {!isActive && <span className="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground" data-testid={`user-inactive-badge-${u.id}`}>Inactive</span>}
                  </td>
                  <td className="px-3 py-3 text-muted-foreground break-all" title={u.email}>{u.email}</td>
                  <td className="px-3 py-3"><span className="text-xs uppercase tracking-wide px-2 py-0.5 rounded-full bg-primary/10 text-primary whitespace-nowrap">{u.role.replace("_", " ")}</span></td>
                  <td className="px-3 py-3" data-testid={`user-region-${u.id}`}>
                    {REGION_BY_VALUE[u.region] ? (
                      <span className={`text-xs px-2 py-0.5 rounded-full border whitespace-nowrap ${REGION_BY_VALUE[u.region].tone}`}>
                        {REGION_BY_VALUE[u.region].short}
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground/60">—</span>
                    )}
                  </td>
                  <td className="px-3 py-3 text-muted-foreground break-words" title={ph?.name || ""}>{ph?.name || "—"}</td>
                  <td className="px-3 py-3 text-muted-foreground" data-testid={`user-vehicle-${u.id}`}>
                    {veh ? (
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {veh.color && (
                          <span
                            className="h-3 w-3 rounded-full border border-border shrink-0"
                            style={{ background: VEHICLE_COLOR_HEX[veh.color] || "#888" }}
                            title={veh.color}
                          />
                        )}
                        <span className="font-mono-num text-xs">{veh.plate || "—"}</span>
                        {veh.model && <span className="text-xs text-muted-foreground/80 truncate" title={veh.model}>· {veh.model}</span>}
                      </div>
                    ) : (
                      u.role === "driver" ? <span className="text-xs text-muted-foreground/60">No vehicle</span> : "—"
                    )}
                  </td>
                  <td className="px-3 py-3 text-center">
                    <Checkbox
                      checked={isActive}
                      onCheckedChange={(v) => toggleActive(u, !!v)}
                      data-testid={`user-active-toggle-${u.id}`}
                      aria-label={isActive ? "Deactivate user" : "Activate user"}
                    />
                  </td>
                  <td className="px-3 py-3 text-right whitespace-nowrap">
                    {u.role === "driver" && <HipaaSendButton user={u} row={hipaaByDriver[u.id]} onSent={loadHipaa} />}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => sendInviteEmail(u)}
                      disabled={emailingId === u.id || !u.email}
                      className="text-muted-foreground hover:text-primary h-8 w-8"
                      data-testid={`user-send-invite-${u.id}`}
                      title={u.email ? `Email app invite to ${u.email}` : "No email on file"}
                    >
                      <Mail className={`h-4 w-4 ${emailingId === u.id ? "animate-pulse" : ""}`} />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => startEdit(u)} className="text-muted-foreground hover:text-primary h-8 w-8" data-testid={`user-edit-${u.id}`} title="Edit user">
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => del(u.id)} className="text-muted-foreground hover:text-destructive h-8 w-8" data-testid={`user-delete-${u.id}`} title="Delete user">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              );
            })}
            {filteredRows.length === 0 && (
              <tr>
                <td colSpan={8} className="px-5 py-10 text-center text-sm text-muted-foreground" data-testid="users-empty-state">
                  No team members match these filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        </div>
      </Card>

      {/* Edit user dialog */}
      <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit user</DialogTitle></DialogHeader>
          {editTarget && (
            <form onSubmit={saveEdit} className="space-y-3" data-testid="edit-user-form">
              <div><Label>Full name</Label><Input required value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="edit-user-name" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Email</Label><Input type="email" required value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} data-testid="edit-user-email" /></div>
                <div><Label>Phone</Label><Input value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: formatPhoneInput(e.target.value) })} placeholder="305-555-1234" data-testid="edit-user-phone" /></div>
              </div>
              <div><Label>New password (leave blank to keep)</Label><Input type="password" value={editForm.password} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} data-testid="edit-user-password" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Role</Label>
                  <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })}>
                    <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ROLES.map((r) => <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Pharmacy</Label>
                  <Select value={editForm.pharmacy_id || "none"} onValueChange={(v) => setEditForm({ ...editForm, pharmacy_id: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="edit-user-pharmacy"><SelectValue placeholder="None" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— None —</SelectItem>
                      {pharmacies.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Workspace / area</Label>
                <Select value={editForm.region || "south_fl"} onValueChange={(v) => setEditForm({ ...editForm, region: v })}>
                  <SelectTrigger data-testid="edit-user-region"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {REGIONS.map((r) => (
                      <SelectItem key={r.v} value={r.v} data-testid={`edit-user-region-${r.v}`}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {editForm.role === "driver" && (
                <div className="rounded-lg border border-border/70 bg-secondary/30 p-3 space-y-3" data-testid="edit-user-vehicle-section">
                  <div className="flex items-center gap-2">
                    <Car className="h-4 w-4 text-primary" />
                    <span className="text-sm font-semibold">Vehicle</span>
                    <span className="text-xs text-muted-foreground">
                      {vehicleByDriverId[editTarget?.id] ? "· Editing existing" : "· Adds a new one"}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Tag / plate</Label>
                      <Input
                        value={editForm.veh_plate}
                        onChange={(e) => setEditForm({ ...editForm, veh_plate: e.target.value })}
                        placeholder="ABC-1234"
                        data-testid="edit-user-veh-plate"
                      />
                    </div>
                    <div>
                      <Label>Model</Label>
                      <Input
                        value={editForm.veh_model}
                        onChange={(e) => setEditForm({ ...editForm, veh_model: e.target.value })}
                        placeholder="Toyota Camry"
                        data-testid="edit-user-veh-model"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Color</Label>
                    <Select value={editForm.veh_color || "White"} onValueChange={(v) => setEditForm({ ...editForm, veh_color: v })}>
                      <SelectTrigger data-testid="edit-user-veh-color">
                        <SelectValue>
                          <span className="inline-flex items-center gap-2">
                            <span
                              className="h-3.5 w-3.5 rounded-full border border-border"
                              style={{ background: VEHICLE_COLOR_HEX[editForm.veh_color] || "#888" }}
                            />
                            {editForm.veh_color}
                          </span>
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {VEHICLE_COLOR_OPTIONS.map((name) => (
                          <SelectItem key={name} value={name}>
                            <span className="inline-flex items-center gap-2">
                              <span
                                className="h-3.5 w-3.5 rounded-full border border-border"
                                style={{ background: VEHICLE_COLOR_HEX[name] }}
                              />
                              {name}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
              <Button type="submit" disabled={busy} className="w-full rounded-full" data-testid="edit-user-submit">{busy ? "Saving…" : "Save changes"}</Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Email-sent / fallback link dialog */}
      <Dialog open={!!inviteResult} onOpenChange={(o) => !o && setInviteResult(null)}>
        <DialogContent data-testid="invite-result-dialog">
          <DialogHeader>
            <DialogTitle>
              {inviteResult?.email_sent
                ? `Invite emailed to ${inviteResult?.user?.name}`
                : `Couldn't email ${inviteResult?.user?.name}`}
            </DialogTitle>
          </DialogHeader>
          {inviteResult && (
            <div className="space-y-4">
              {inviteResult.email_sent ? (
                <div className="rounded-lg border border-accent/40 bg-accent/10 text-accent-foreground/90 px-3 py-2.5 text-sm flex items-start gap-2" data-testid="invite-success-banner">
                  <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-accent" />
                  <span>Email sent to <strong>{inviteResult.to}</strong>. They'll be auto-signed in for <strong>{inviteResult.expires_in_days} days</strong>.</span>
                </div>
              ) : (
                <div className="rounded-lg border border-amber-300 bg-amber-50 text-amber-900 px-3 py-2.5 text-sm flex items-start gap-2" data-testid="invite-fallback-banner">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <div>
                    <div className="font-medium">Couldn't deliver the email automatically.</div>
                    <div className="text-xs mt-0.5">
                      {/Resend|domain|verified|testing/i.test(inviteResult.error || "")
                        ? "Your Resend account is in testing mode (only verified addresses receive mail). Verify a sending domain at resend.com/domains to send to anyone."
                        : (inviteResult.error || "Unknown error.")}
                    </div>
                    <div className="text-xs mt-1.5">Meanwhile, scan the QR or copy the link below — it auto-signs them in for {inviteResult.expires_in_days} days.</div>
                  </div>
                </div>
              )}

              {inviteResult.fallback_url && (
                <div className="flex items-center justify-center bg-white rounded-xl p-4 border border-border" data-testid="invite-qr-wrap">
                  <img
                    alt="QR code"
                    width={200}
                    height={200}
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&margin=8&data=${encodeURIComponent(inviteResult.fallback_url)}`}
                    data-testid="invite-qr"
                  />
                </div>
              )}
              <p className="text-[11px] text-muted-foreground text-center -mt-2">
                Show this QR to {inviteResult.user?.name || "the user"} — they can scan it with their phone camera and sign in instantly.
              </p>

              <div>
                <Label className="text-xs text-muted-foreground">Magic invite link</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    value={inviteResult.fallback_url || ""}
                    readOnly
                    className="text-xs font-mono"
                    data-testid="invite-fallback-url"
                    onFocus={(e) => e.target.select()}
                  />
                  <Button
                    onClick={copyInvite}
                    className="rounded-full shrink-0"
                    data-testid="invite-fallback-copy-btn"
                  >
                    {inviteResult.copied ? <CheckCircle2 className="h-4 w-4 mr-1.5" /> : <Copy className="h-4 w-4 mr-1.5" />}
                    {inviteResult.copied ? "Copied" : "Copy link"}
                  </Button>
                </div>
                <p className="text-[11px] text-muted-foreground mt-1.5">
                  Send this via WhatsApp, SMS, or any chat tool. Opening it on any device will sign them in instantly.
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
